export const brand = {
  name: 'KINDERHANDBALL',
  claim: 'Mit Plan ins Training.',
}

/**
 * Commercial configuration.
 * The final price is confirmed (19 EUR). The checkout URL is not confirmed yet,
 * so it stays null and every CTA navigates to the content sections instead.
 */
export const offer = {
  price: 19,
  currency: 'EUR',
  priceLabel: '19 €',
  paymentLabel: 'Einmalzahlung',
  checkoutUrl: null as string | null,
  /** Used only once a checkout URL is configured. */
  checkoutCtaLabel: 'Jetzt für 19 € sichern',
  draftCtaLabel: 'Inhalte ansehen',
}

export const heroChips = ['6 PDF-Bände', '360 Seiten', '6–12 Jahre']

export const keyFigures = [
  { value: '6', label: 'Bände' },
  { value: '360', label: 'Seiten' },
  { value: '3', label: 'Altersstufen' },
  { value: '1', label: 'roter Faden' },
]

export type Band = {
  number: string
  title: string
  group: string
  age: string
  focus: string
}

export const bands: Band[] = [
  {
    number: '01',
    title: 'Spielerisch mit Ball starten',
    group: 'Minis und F-Jugend',
    age: '6–8 Jahre',
    focus: 'Laufen, Springen, Werfen, Fangen und erste Ballspiele.',
  },
  {
    number: '02',
    title: 'Gemeinsam Handball entdecken',
    group: 'Minis und F-Jugend',
    age: '6–8 Jahre',
    focus: 'Zusammenspielen, freie Räume erkennen, kleine Handballspiele.',
  },
  {
    number: '03',
    title: 'Die Grundlagen sicher beherrschen',
    group: 'E-Jugend',
    age: '9–10 Jahre',
    focus: 'Passen, Fangen, Prellen und Werfen.',
  },
  {
    number: '04',
    title: 'Im Spiel die richtige Entscheidung treffen',
    group: 'E-Jugend',
    age: '9–10 Jahre',
    focus: 'Freilaufen, Eins gegen Eins, Zusammenspiel und aktives Verteidigen.',
  },
  {
    number: '05',
    title: 'Technik gezielt im Spiel einsetzen',
    group: 'D-Jugend',
    age: '11–12 Jahre',
    focus: 'Technik unter Gegnerdruck und individuelle Lösungen.',
  },
  {
    number: '06',
    title: 'Zusammenspielen und das Spiel verstehen',
    group: 'D-Jugend',
    age: '11–12 Jahre',
    focus: 'Gemeinsam angreifen, verteidigen und umschalten.',
  },
]

export type Stage = {
  age: string
  group: string
  headline: string
  description: string
  bandNumbers: string[]
}

export const stages: Stage[] = [
  {
    age: '6–8 Jahre',
    group: 'Minis und F-Jugend',
    headline: 'Einstieg mit Ball',
    description:
      'Bewegen, Werfen und Fangen in einfachen Spielformen. Sechs Jahre bedeutet Einstieg auf Mini-Niveau.',
    bandNumbers: ['01', '02'],
  },
  {
    age: '9–10 Jahre',
    group: 'E-Jugend',
    headline: 'Grundlagen und Entscheidungen',
    description:
      'Technische Grundlagen festigen und sie im Spiel bewusst einsetzen.',
    bandNumbers: ['03', '04'],
  },
  {
    age: '11–12 Jahre',
    group: 'D-Jugend',
    headline: 'Technik und Spielverständnis',
    description:
      'Technik unter Gegnerdruck anwenden, gemeinsam angreifen und umschalten.',
    bandNumbers: ['05', '06'],
  },
]

export const painPoints = [
  'Übungen liegen über Videos, Notizen und alte Zettel verteilt.',
  'In der Halle treffen sehr unterschiedliche Lernstände aufeinander.',
  'Der Aufbau über die Saison bleibt unklar.',
  'Jede Einheit wird wieder von Neuem zusammengestellt.',
]

export const practiceBenefits = [
  {
    title: 'Klare Organisation und klare Ziele',
    description:
      'Jede Trainingseinheit ist nachvollziehbar aufgebaut und benennt, worum es in der Einheit geht.',
  },
  {
    title: 'Übungen in visuellen Schritten',
    description:
      'Die Übungsbeschreibungen sind in Schritte gegliedert und mit Aufstellungen anschaulich dargestellt.',
  },
  {
    title: 'Varianten je nach Lernstand',
    description:
      'Zu den Übungen gibt es einfachere und anspruchsvollere Varianten für unterschiedliche Gruppen.',
  },
  {
    title: 'Hinweise zum Beobachten',
    description:
      'Beobachtungshinweise helfen dir, die Arbeit im Training passend anzupassen.',
  },
]

export const usageSteps = [
  {
    step: '1',
    title: 'Altersstufe wählen',
    description:
      'Du entscheidest, welche Altersstufe und welcher Band zu deiner Gruppe passen. Die Kategorien sind eine Orientierung, kein festes Raster.',
  },
  {
    step: '2',
    title: 'Einheit vorbereiten',
    description:
      'Du prüfst Hallenfläche, Material und die Bedürfnisse der Gruppe und wählst die passende Einheit und Variante aus.',
  },
  {
    step: '3',
    title: 'Beobachten und anpassen',
    description:
      'Im Training beobachtest du die Kinder und passt Umfang, Schwierigkeit und Organisation an.',
  },
]

export const useCases = [
  {
    title: 'Für den Verein',
    description:
      'Eine gemeinsame Grundlage für mehrere Jahrgänge, an der sich Trainerinnen und Trainer im Verein orientieren können.',
  },
  {
    title: 'Für die Sporthalle',
    description:
      'Einheiten für den Hallenbetrieb mit klaren Aufstellungen, die sich an Fläche und Material anpassen lassen.',
  },
  {
    title: 'Für gemischte Lernstände',
    description:
      'Varianten zu den Übungen, damit Kinder mit unterschiedlichem Stand in derselben Einheit arbeiten können.',
  },
]

export const offerIncludes = [
  '6 PDF-Bände',
  '360 Seiten',
  '6–12 Jahre',
  '3 Altersstufen',
  'Auf Deutsch',
  'Digitale Sammlung, kein Versand',
]

export const faqs = [
  {
    question: 'Was ist enthalten?',
    answer:
      'Die Sammlung besteht aus sechs digitalen PDF-Bänden mit je 60 Seiten, insgesamt 360 Seiten. Jeder Band ist einer Altersstufe und einem inhaltlichen Schwerpunkt zugeordnet.',
  },
  {
    question: 'Für welches Alter ist die Sammlung gedacht?',
    answer:
      'Für Kinder von 6 bis 12 Jahren, aufgeteilt in drei Altersstufen: 6–8 Jahre (Minis und F-Jugend), 9–10 Jahre (E-Jugend) und 11–12 Jahre (D-Jugend). Mit sechs Jahren steht der Einstieg auf Mini-Niveau im Vordergrund.',
  },
  {
    question: 'Für wen ist die Sammlung gedacht?',
    answer:
      'Für Trainerinnen, Trainer und Lehrkräfte, die Kinderhandball planen und anleiten. Gruppe, Lernstand und örtliche Bedingungen werden von den Erwachsenen selbst eingeschätzt.',
  },
  {
    question: 'Sind das gedruckte Bücher?',
    answer:
      'Nein. Es handelt sich um digitale PDF-Bände. Es gibt keinen physischen Versand.',
  },
  {
    question: 'Kann ich die Inhalte am Bildschirm nutzen?',
    answer:
      'Ja. Die Bände sind PDFs und lassen sich auf Geräten mit einem PDF-Reader lesen.',
  },
  {
    question: 'Muss ich die Übungen anpassen?',
    answer:
      'Ja. Die Einheiten sind so angelegt, dass du sie an Lernstand, Hallenfläche und Gruppengröße anpasst. Die Altersangaben dienen der Orientierung.',
  },
]
